"use client";

import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, PoundSterling, MessageSquare } from 'lucide-react';

interface Address {
  city: string;
  postcode: string;
}

interface Job {
  id: string;
  title: string;
  type: string;
  price: number;
  budget: number;
  dDate: string;
  dTime: string;
  fromAddress: string | Address;
  toAddress: string | Address;
  status: string;
}

interface JobCardProps {
  job: Job;
  variant?: 'active' | 'completed';
}

export function JobCard({ job, variant = 'active' }: JobCardProps) {
  const router = useRouter();
  const formatJobType = (type: string) =>
    type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

  const shortAddress = (address: Job['fromAddress']) => {
    if (typeof address === 'string') {
      const parts = address.split(',').map((p) => p.trim());
      if (parts.length >= 2) {
        const country = parts.pop();
        const district = parts.pop();
        return `${district}, ${country}`;
      }
      return address;
    }
    return `${address.city}, ${address.postcode.slice(0, 4)}`;
  };

  const fullAddress = (address: Job['fromAddress']) =>
    typeof address === 'string' ? address : `${address.city} ${address.postcode}`;

  const shortFrom = shortAddress(job.fromAddress);
  const shortTo = shortAddress(job.toAddress);

  const showChat = variant === 'active' && job.status.toLowerCase() === 'accepted';

  return (
    <Card
      tabIndex={0}
      className={cn(
        'cursor-pointer border-l-4 hover:shadow-lg hover:scale-[1.02] transition-transform transition-shadow duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        variant === 'active' ? 'border-primary' : 'border-green-500'
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="flex flex-col">
            <CardTitle className="text-lg">{job.title}</CardTitle>
            <span className="text-sm text-foreground">
              {formatJobType(job.type).replace(/[()]/g, '')}
            </span>
          </div>
          {variant === 'active' ? (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">£{job.price}</span>
            </div>
          ) : (
            <span className="bg-green-100 text-green-800 text-xs px-2.5 py-0.5 rounded-full whitespace-nowrap">
              Completed
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>
              {new Date(job.dDate).toLocaleDateString()} at {job.dTime}
            </span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
            <div className="flex flex-col truncate">
              <span className="font-medium">Pickup:</span>
              <span className="truncate">{showChat ? fullAddress(job.fromAddress) : shortFrom}</span>
              <span className="font-medium mt-2">Delivery:</span>
              <span className="truncate">{showChat ? fullAddress(job.toAddress) : shortTo}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <PoundSterling className="h-4 w-4 text-muted-foreground" />
            <span>£{job.budget} budget</span>
          </div>
          {showChat && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.push(`/chat/${job.id}`)}
            >
              <MessageSquare className="h-4 w-4 mr-1" /> Chat
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}